import React, { FC, useEffect } from 'react';
import triggerTracking from '@blueprint/tracking';
import { FacebookColourMedium, GoogleColourMedium } from '@blueprint/icons';

import {
    SocialSignInWrapper,
    SocialSignInText,
    SignInErrorMessage
} from '../../AuthModal.styles';
import { ButtonWithLoading } from 'shared/components/ButtonWithLoading';
import { useSocialSignIn } from './hooks/useSocialSignIn/useSocialSignIn';
import { ActionAfterAuth } from '../../AuthModal.types';
import ErrorAlert from '../../ErrorAlert/ErrorAlert';

type SocialCopy = {
    cta: string;
    facebook: string;
    google: string;
};

export type LoadingType = Partial<Record<'facebook' | 'google', boolean>>;

export type SocialAuthComponentProps = {
    actionAfterAuth: ActionAfterAuth;
    copy: SocialCopy;
    type: 'signin' | 'register';
    shouldReset?: boolean;
    resetErrors?: () => void;
};

const SocialAuth: FC<SocialAuthComponentProps> = ({
    actionAfterAuth,
    copy,
    type,
    shouldReset,
    resetErrors
}) => {
    const [
        facebookError,
        onFacebookRegister,
        isFacebookAuthenticating,
        resetFacebookError
    ] = useSocialSignIn({
        type: 'facebook',
        actionAfterAuth
    });

    const [
        googleError,
        onGoogleRegister,
        isGoogleAuthenticating,
        resetGoogleError
    ] = useSocialSignIn({
        type: 'google',
        actionAfterAuth
    });

    useEffect(() => {
        if (isFacebookAuthenticating || isGoogleAuthenticating) {
            resetErrors?.();
            return;
        }

        if (shouldReset) {
            resetGoogleError();
            resetFacebookError();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFacebookAuthenticating, isGoogleAuthenticating, shouldReset]);

    const socialError = googleError || facebookError;

    return (
        <>
            <ErrorAlert hasError={!!socialError}>
                <SignInErrorMessage
                    data-testid="social-error"
                    variant="invalid"
                    message={socialError}
                />
            </ErrorAlert>
            <SocialSignInWrapper className="social-login">
                <SocialSignInText color="grey-steel" size="secondary">
                    {copy.cta}
                </SocialSignInText>
                <ButtonWithLoading
                    variant="secondary"
                    isFullWidth
                    data-testid={`${type}-google`}
                    onClick={() => {
                        onGoogleRegister();
                        triggerTracking({
                            event: 'GAEvent',
                            eventCategory: type,
                            eventAction: 'click',
                            eventLabel: 'google'
                        });
                    }}
                    isLoading={isGoogleAuthenticating}
                >
                    <GoogleColourMedium />
                    {copy.google}
                </ButtonWithLoading>
                <ButtonWithLoading
                    variant="secondary"
                    isFullWidth
                    data-testid={`${type}-facebook`}
                    onClick={() => {
                        onFacebookRegister();
                        triggerTracking({
                            event: 'GAEvent',
                            eventCategory: type,
                            eventAction: 'click',
                            eventLabel: 'facebook'
                        });
                    }}
                    isLoading={isFacebookAuthenticating}
                >
                    <FacebookColourMedium />
                    {copy.facebook}
                </ButtonWithLoading>
            </SocialSignInWrapper>
        </>
    );
};

export default SocialAuth;
